import type {
  TeacherPortfolioTaskShallowResource,
  TeacherPortfolioTaskTemplateShallowResource,
  TeacherPublicPortfolioTaskShallowResource,
} from "api/generated";
import type {
  TeacherLibraryTask,
  TeacherPublicLibraryTask,
  TeacherScheduledTask,
  TeacherTaskMeta,
  TeacherTaskProgress,
} from "portfolio/types";
import { mapShallowTeacher } from "shared/mappers";
import {
  baseTagFromDto,
  publicTagFromDto,
} from "portfolio/api/mappers/tag.mapper";

const mapTaskMeta = (
  meta: TeacherPortfolioTaskShallowResource["meta"],
): TeacherTaskMeta => ({
  canUpdate: meta.can_update,
  canDelete: meta.can_delete,
});

const mapPublicTaskMeta = (
  meta: TeacherPublicPortfolioTaskShallowResource["meta"],
): TeacherTaskMeta => ({
  canUpdate: meta.can_update,
  canDelete: meta.can_delete,
});

const mapTaskProgress = (
  progress: TeacherPortfolioTaskShallowResource["progress"],
): TeacherTaskProgress => ({
  totalStudents: progress.total_students,
  completedTaskAmount: progress.completed_task_amount,
});

export const teacherScheduledTaskFromDto = (
  resource: TeacherPortfolioTaskShallowResource,
): TeacherScheduledTask => ({
  id: Number(resource.id),
  title: resource.title,
  description: resource.description,
  bannerImage: resource.banner_image,
  tags: resource.tags.map(baseTagFromDto),
  createdAt: resource.created_at ? new Date(resource.created_at) : null,
  updatedAt: resource.updated_at ? new Date(resource.updated_at) : null,
  meta: mapTaskMeta(resource.meta),
  type: "task",
  owners: resource.owners.map(mapShallowTeacher),
  user: resource.user ? mapShallowTeacher(resource.user) : null,
  status: resource.status,
  startDate: resource.start_date ? new Date(resource.start_date) : null,
  deadline: resource.deadline ? new Date(resource.deadline) : null,
  progress: mapTaskProgress(resource.progress),
});

export const teacherLibraryTaskFromDto = (
  resource: TeacherPortfolioTaskTemplateShallowResource,
): TeacherLibraryTask => ({
  id: Number(resource.id),
  title: resource.title,
  description: resource.description,
  bannerImage: resource.banner_image,
  tags: resource.tags.map(baseTagFromDto),
  createdAt: resource.created_at ? new Date(resource.created_at) : null,
  updatedAt: resource.updated_at ? new Date(resource.updated_at) : null,
  meta: mapTaskMeta(resource.meta),
  type: "task_template",
  owners: resource.owners.map(mapShallowTeacher),
  user: resource.user ? mapShallowTeacher(resource.user) : null,
});

export const teacherPublicLibraryTaskFromDto = (
  resource: TeacherPublicPortfolioTaskShallowResource,
): TeacherPublicLibraryTask => ({
  id: Number(resource.id),
  title: resource.title,
  description: resource.description,
  bannerImage: resource.banner_image,
  tags: resource.tags.map(publicTagFromDto),
  createdAt: resource.created_at ? new Date(resource.created_at) : null,
  updatedAt: resource.updated_at ? new Date(resource.updated_at) : null,
  meta: mapPublicTaskMeta(resource.meta),
  type: "task_template",
  user: null,
});
